/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file driverDailyCheck.js
 * @description Pure logic of the driver's "Napi ellenőrzőlap" (daily pre-shift
 * check): turning the server template into wizard steps, answer visibility /
 * validation (incl. `requiredWhen`), the submit payloads, photo vocabulary and
 * the error → i18n mapping. No React, no network — see
 * `screens/driver/DriverDailyCheckScreen.js` and `helpers/driverApi.js`.
 *
 * @module helpers/driverDailyCheck
 */

/** Body-area chips of a damage photo, in display order. */
export const PHOTO_AREAS = [
    {key: 'front', icon: 'car'},
    {key: 'rear', icon: 'car-back'},
    {key: 'left', icon: 'car-side'},
    {key: 'right', icon: 'car-side'},
    {key: 'interior', icon: 'car-seat'},
    {key: 'cargo', icon: 'package-variant-closed'},
    {key: 'other', icon: 'dots-horizontal-circle-outline'},
];

/** Severity of a photographed problem, mildest first. */
export const PHOTO_SEVERITIES = [
    {key: 'info', tone: 'info', icon: 'information-outline'},
    {key: 'minor', tone: 'warning', icon: 'alert-outline'},
    {key: 'major', tone: 'danger', icon: 'alert-octagon-outline'},
    {key: 'crashed', tone: 'danger', icon: 'car-emergency'},
];

/** Client-side cap of photos in one request (the server answers TOO_MANY_PHOTOS above its own). */
export const MAX_PHOTOS_PER_REQUEST = 10;

/** Max length of a photo note. */
export const PHOTO_NOTE_MAX = 500;

/** Fallback max length of a text answer when the template omits `maxLength`. */
export const TEXT_MAX_DEFAULT = 500;

/** Machine `issues` the server may report on a check. */
export const PLATE_MISMATCH_ISSUE = 'plateMismatch';

/**
 * Upper-case a plate and strip everything but letters, digits and dashes.
 * @param {*} value Raw input.
 * @returns {string} `ABC-123`-style text.
 */
export const normalizePlate = (value) => String(value ?? '')
    .toUpperCase()
    .replace(/\s+/g, '')
    .replace(/[^A-Z0-9-]/g, '')
    .slice(0, 12);

/**
 * Whether a normalized plate looks like a plate (≥ 3 letters/digits).
 * @param {string} plate Plate text.
 * @returns {boolean} True when plausible.
 */
export const isPlausiblePlate = (plate) => normalizePlate(plate).replace(/-/g, '').length >= 3;

/**
 * Coerce the `today` payload into a safe shape (every list an array).
 * @param {?object} data Unwrapped `GET /driver/daily-checks/today` payload.
 * @returns {{date: string, template: {sections: Array<object>, questions: Array<object>},
 *   check: ?object, vehicle: ?object}} Normalized payload.
 */
export const normalizeToday = (data) => ({
    date: data?.date || '',
    template: {
        sections: Array.isArray(data?.template?.sections) ? data.template.sections : [],
        questions: Array.isArray(data?.template?.questions) ? data.template.questions : [],
    },
    check: data?.check || null,
    vehicle: data?.vehicle || null,
});

/**
 * Whether a question is shown for the current answers (a `requiredWhen` follow-up
 * only appears once its trigger holds the expected value).
 * @param {object} question Template question.
 * @param {Object<string, *>} answers Current answers.
 * @returns {boolean} True when visible.
 */
export const isQuestionVisible = (question, answers) => {
    const rule = question?.requiredWhen;

    if (!rule || !rule.key) {
        return true;
    }

    return answers?.[rule.key] === rule.equals;
};

/**
 * Whether a visible question must be answered.
 * @param {object} question Template question.
 * @param {Object<string, *>} answers Current answers.
 * @returns {boolean} True when an answer is mandatory.
 */
export const isQuestionRequired = (question, answers) => {
    if (!isQuestionVisible(question, answers)) {
        return false;
    }

    return !!question?.required || !!question?.requiredWhen;
};

/**
 * Whether a value counts as "answered".
 * @param {object} question Template question.
 * @param {*} value Current value.
 * @returns {boolean} True when answered.
 */
const hasAnswer = (question, value) => {
    if (question?.type === 'yesno') {
        return typeof value === 'boolean';
    }

    return typeof value === 'string' && value.trim().length > 0;
};

/**
 * Validate the answers against the template.
 * @param {{questions: Array<object>}} template Server template.
 * @param {Object<string, *>} answers Current answers.
 * @returns {Object<string, 'required'|'tooLong'|'invalid'>} Map key → reason (empty when valid).
 */
export const validateAnswers = (template, answers) => {
    const errors = {};

    (template?.questions || []).forEach((question) => {
        const value = answers?.[question.key];

        if (!isQuestionVisible(question, answers)) {
            return;
        }

        if (!hasAnswer(question, value)) {
            if (isQuestionRequired(question, answers)) {
                errors[question.key] = 'required';
            }

            return;
        }

        if (question.type === 'plate') {
            if (!isPlausiblePlate(value)) {
                errors[question.key] = 'invalid';
            }
        } else if (question.type !== 'yesno' && String(value).trim().length > (Number(question.maxLength) || TEXT_MAX_DEFAULT)) {
            errors[question.key] = 'tooLong';
        }
    });

    return errors;
};

/**
 * Build the `answers` object of the POST body: visible questions only, text
 * trimmed, plate normalized, empty optional answers dropped.
 * @param {{questions: Array<object>}} template Server template.
 * @param {Object<string, *>} answers Current answers.
 * @returns {Object<string, boolean|string>} Submit-ready answers.
 */
export const buildAnswersPayload = (template, answers) => {
    const payload = {};

    (template?.questions || []).forEach((question) => {
        const value = answers?.[question.key];

        if (!isQuestionVisible(question, answers) || !hasAnswer(question, value)) {
            return;
        }

        if (question.type === 'yesno') {
            payload[question.key] = value;
        } else if (question.type === 'plate') {
            payload[question.key] = normalizePlate(value);
        } else {
            payload[question.key] = String(value).trim();
        }
    });

    return payload;
};

/**
 * Turn the template into wizard steps. One step per primary question (a plate,
 * a yes/no, or a standalone text) with its `requiredWhen` follow-ups attached,
 * in server section order then server question order; followed by the photo and
 * review steps.
 * @param {{sections?: Array<object>, questions: Array<object>}} template Server template.
 * @returns {Array<{id: string, kind: 'question'|'photos'|'review', section?: string,
 *   question?: object, followUps?: Array<object>}>} Steps.
 */
export const buildSteps = (template) => {
    const questions = Array.isArray(template?.questions) ? template.questions : [];
    const sectionOrder = (Array.isArray(template?.sections) ? template.sections : []).map((section) => (typeof section === 'string' ? section : section?.key));
    const rank = (key) => {
        const index = sectionOrder.indexOf(key);

        return index === -1 ? sectionOrder.length : index;
    };
    const ordered = questions
        .map((question, index) => ({question, index}))
        .sort((a, b) => (rank(a.question.section) - rank(b.question.section)) || (a.index - b.index))
        .map((entry) => entry.question);
    const keys = new Set(ordered.map((question) => question.key));
    const isFollowUp = (question) => !!question.requiredWhen?.key && keys.has(question.requiredWhen.key);
    const steps = ordered.filter((question) => !isFollowUp(question)).map((question) => ({
        id: question.key,
        kind: 'question',
        section: question.section,
        question,
        followUps: ordered.filter((other) => isFollowUp(other) && other.requiredWhen.key === question.key),
    }));

    return [...steps, {id: 'photos', kind: 'photos'}, {id: 'review', kind: 'review'}];
};

/**
 * Keys of the questions a step shows (primary + its follow-ups).
 * @param {object} step Wizard step.
 * @returns {string[]} Keys.
 */
export const stepKeys = (step) => (step?.kind === 'question'
    ? [step.question.key, ...(step.followUps || []).map((followUp) => followUp.key)]
    : []);

/**
 * Whether a question step holds valid answers (so "Tovább" may be enabled).
 * @param {object} step Wizard step.
 * @param {Object<string, *>} answers Current answers.
 * @returns {boolean} True when complete (non-question steps are always complete).
 */
export const isStepComplete = (step, answers) => {
    if (step?.kind !== 'question') {
        return true;
    }

    const errors = validateAnswers({questions: [step.question, ...(step.followUps || [])]}, answers);

    return Object.keys(errors).length === 0;
};

/**
 * Whether tapping an answer on this step may advance on its own: a yes/no
 * answered `true` with no follow-up pending, or answered at all without any
 * follow-up that the answer would reveal.
 * @param {object} step Wizard step.
 * @param {Object<string, *>} answers Answers AFTER the tap.
 * @returns {boolean} True when the wizard may move on at once.
 */
export const shouldAutoAdvance = (step, answers) => (
    step?.kind === 'question'
    && step.question?.type === 'yesno'
    && isStepComplete(step, answers)
);

/**
 * Index of the first step that owns one of the failing keys (`-1` when none).
 * @param {Array<object>} steps Wizard steps.
 * @param {Object<string, *>} errors Map key → reason (client or server `data.fields`).
 * @returns {number} Step index.
 */
export const firstInvalidStepIndex = (steps, errors) => {
    const failing = new Set(Object.keys(errors || {}));

    return (steps || []).findIndex((step) => stepKeys(step).some((key) => failing.has(key)));
};

/**
 * Progress 0..1 of the wizard (the review step counts as the full bar).
 * @param {number} index Current step index.
 * @param {number} total Step count.
 * @returns {number} Fraction.
 */
export const stepProgress = (index, total) => (total > 1 ? Math.max(0, Math.min(1, index / (total - 1))) : 0);

/**
 * Build one photo entry of the POST body.
 * @param {{documentId: string, area?: string, severity?: string, note?: string, takenAt?: (string|Date)}} photo Draft.
 * @returns {{documentId: string, area: string, severity: string, note: string, takenAt: string}} Entry.
 */
export const buildPhotoPayload = (photo) => {
    const area = PHOTO_AREAS.some((item) => item.key === photo?.area) ? photo.area : 'other';
    const severity = PHOTO_SEVERITIES.some((item) => item.key === photo?.severity) ? photo.severity : 'info';
    const taken = photo?.takenAt ? new Date(photo.takenAt) : new Date();

    return {
        documentId: String(photo?.documentId || ''),
        area,
        severity,
        note: String(photo?.note || '').trim().slice(0, PHOTO_NOTE_MAX),
        takenAt: (Number.isNaN(taken.getTime()) ? new Date() : taken).toISOString(),
    };
};

/**
 * Build the `photos` array of a request from local drafts: only uploaded ones
 * (a `documentId`, not failed / still uploading), capped.
 * @param {Array<object>} drafts Local photo drafts.
 * @returns {Array<object>} Photo entries.
 */
export const buildPhotosPayload = (drafts) => (Array.isArray(drafts) ? drafts : [])
    .filter((draft) => draft?.documentId && !draft.uploading && !draft.failed)
    .slice(0, MAX_PHOTOS_PER_REQUEST)
    .map(buildPhotoPayload);

/**
 * Whether a draft list still has uploads in flight.
 * @param {Array<object>} drafts Local photo drafts.
 * @returns {boolean} True while any photo uploads.
 */
export const hasPendingUploads = (drafts) => (Array.isArray(drafts) ? drafts : []).some((draft) => draft?.uploading);

/**
 * Whether the server flagged the entered plate as not the assigned one.
 * @param {?object} check DriverDailyCheck.
 * @returns {boolean} True on a plate mismatch.
 */
export const hasPlateMismatch = (check) => !!check && (
    check.plateMatch === false || (Array.isArray(check.issues) && check.issues.includes(PLATE_MISMATCH_ISSUE))
);

/**
 * State of today's check for the home card.
 * @param {?object} check DriverDailyCheck or null.
 * @returns {'todo'|'ok'|'attention'} State.
 */
export const checkCardState = (check) => {
    if (!check) {
        return 'todo';
    }

    return check.status === 'attention' || hasPlateMismatch(check) ? 'attention' : 'ok';
};

/**
 * Human fallback label of an unknown template key (`someNewKey` → `Some new key`).
 * @param {string} key Template key.
 * @returns {string} Readable text.
 */
export const humanizeKey = (key) => {
    const spaced = String(key || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim().toLowerCase();

    return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : '';
};

/**
 * Rows of the review step / result: every visible answered question with a
 * "flagged" bit (a `Nem`, or a free-text problem).
 * @param {{questions: Array<object>}} template Server template.
 * @param {Object<string, *>} answers Answers.
 * @returns {Array<{key: string, type: string, section: string, value: (boolean|string), flagged: boolean}>} Rows.
 */
export const buildReviewRows = (template, answers) => {
    const payload = buildAnswersPayload(template, answers);

    return (template?.questions || [])
        .filter((question) => question.key in payload)
        .map((question) => ({
            key: question.key,
            type: question.type,
            section: question.section,
            value: payload[question.key],
            flagged: question.type === 'yesno' ? payload[question.key] === false : !!question.requiredWhen,
        }));
};

/**
 * i18n key of a failed check call.
 * @param {string} code Machine code (`driverErrorCode`).
 * @param {number} [status] HTTP status.
 * @returns {string} `driverCheck_err_*` key.
 */
export const checkErrorKey = (code, status) => {
    switch (code) {
        case 'INVALID_ANSWERS':
            return 'driverCheck_err_invalid_answers';
        case 'INVALID_PHOTO':
            return 'driverCheck_err_invalid_photo';
        case 'TOO_MANY_PHOTOS':
            return 'driverCheck_err_too_many_photos';
        case 'NO_VEHICLE':
            return 'driverCheck_err_no_vehicle';
        case 'CHECK_ALREADY_SUBMITTED':
            return 'driverCheck_err_already';
        default:
            return status === 403 ? 'driverCheck_err_forbidden' : 'driverCheck_err_generic';
    }
};

/**
 * Read the `fields` map of a 422 INVALID_ANSWERS detail.
 * @param {?object} detail `driverErrorData(error)`.
 * @returns {Object<string, string>} Map key → reason.
 */
export const invalidAnswerFields = (detail) => {
    const fields = detail?.fields;

    return fields && typeof fields === 'object' && !Array.isArray(fields) ? fields : {};
};
